/* screens/academy/ReviewDrawer.jsx — проверка сдачи ДЗ куратором.
   Контекст ученика · задание · ответ · прошлые попытки · история проверок ·
   решение (в футере дровера).

   Контракт CU-1 (читаем ровно эти ключи, каждый необязателен — старый бэк их не
   отдаёт, панель тогда просто не рисуется):
     student_context: { progress_pct, lessons_completed, lessons_total,
                        enrollment_status, tariff:{id,title}|null, expires_at,
                        feedback_until, feedback_active }
     past_attempts: [{ id, attempt_no, status, body, created_at,
                       attachments:[{id,name,url}], reviews:[…как в reviews] }]
   PII: email ученика куратору не отдаётся (serializers.get_student_email), и в
   разметке он не используется вовсе — подпись всегда student_label. */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { QueryBoundary, EmptyState } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Pill } from '../../components/ui/Badge.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Field, Textarea } from '../../components/ui/Field.jsx';
import { ChatMarkdown } from '../../components/patterns/chat/Markdown.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/academy.js';
import { ACCESS_TZ } from '../../lib/academy/access.js';
import { RetakeQueue } from './RetakeRequests.jsx';

const { useState, useCallback, useEffect } = React;
const { useFakeQuery } = hooks;

const DECISIONS = [
  { value: 'accepted', label: 'Принять' },
  { value: 'revision', label: 'На доработку' },
  { value: 'rejected', label: 'Отклонить' },
];
// 'reopened' — запись CU-4: вердикт откачен, работа снова в очереди. В журнале
// она стоит рядом с остальными, потому что журнал append-only: отменённый вердикт
// не исчезает, к нему добавляется причина отката.
const DEC_TONE = { accepted: 'ok', revision: 'warn', rejected: 'bad', reopened: 'neutral' };
const DEC_LABEL = { accepted: 'Принято', revision: 'На доработку', rejected: 'Отклонено',
  reopened: 'Переоткрыто' };
const SUB_LABEL = { submitted: 'На проверке', accepted: 'Принято', revision: 'На доработку', rejected: 'Отклонено' };

function fmt(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
/* Срок доступа и окно ОС — даты в поясе платформы (см. ACCESS_TZ): у зрителя
   восточнее Москвы «до 31.12 23:59 МСК» иначе печатается как 01.01. */
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ru-RU',
    { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ACCESS_TZ });
}

function Section({ icon, title, hint, children }) {
  return (
    <section className="acad-section">
      <div className="acad-section__head">
        <Icon name={icon} size={15} /><span className="acad-section__title">{title}</span>
        {hint && <><span className="acad-section__spacer" /><span className="acad-section__hint">{hint}</span></>}
      </div>
      {children}
    </section>
  );
}

// Журнал вердиктов — общий для текущей и прошлых попыток (CU-1).
function ReviewLog({ reviews }) {
  return (
    <div className="acad-history">
      {reviews.map((rv, i) => (
        <div key={i} className="acad-hentry">
          <div className="acad-hentry__rail">
            <span className={'acad-hentry__dot acad-hentry__dot--' + rv.decision} />
            <span className="acad-hentry__line" />
          </div>
          <div className="acad-hentry__body">
            <div className="acad-hentry__head">
              <Pill tone={DEC_TONE[rv.decision]} size="sm" glyph={false}>{DEC_LABEL[rv.decision] || rv.decision}</Pill>
              {rv.score != null && <span className="acad-hentry__score num">{rv.score} б.</span>}
              <span className="acad-hentry__who">{rv.reviewer} · {fmt(rv.created_at)}</span>
            </div>
            {rv.comment && <div className="acad-hentry__comment">{rv.comment}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// Панель контекста ученика: без неё куратор шлёт «на доработку» тому, кому доработка
// уже запрещена тарифом (закрытое окно ОС). Рисуется только если бэк отдал контекст.
function StudentContext({ ctx }) {
  if (!ctx) return null;
  const fbClosed = ctx.feedback_active === false;
  const lessons = (ctx.lessons_completed != null && ctx.lessons_total != null)
    ? `${ctx.lessons_completed} из ${ctx.lessons_total}` : null;
  return (
    <Section icon="user" title="Ученик">
      <div className="acad-student-card__meta">
        <span className="t-caption text-mute">
          Прогресс: <span className="num">{ctx.progress_pct ?? 0}%</span>
          {lessons && <> · уроков <span className="num">{lessons}</span></>}
        </span>
        <span className="t-caption text-mute">Тариф: {ctx.tariff?.title || 'без тарифа'}</span>
        <span className="t-caption text-mute">
          Доступ: {ctx.expires_at ? `до ${fmtDate(ctx.expires_at)}` : 'бессрочно'}
        </span>
        {/* Приоритет причин — как в lesson_lock: заморозка перебивает паузу
            подписки. Куратору это РАЗНЫЕ разговоры: заморозку снимает он сам
            (точнее — организатор), а паузу может снять только продление
            подписки, и по frozen_at её не видно вовсе (он при паузе пуст). */}
        {ctx.frozen_at
          ? <Pill tone="warn" size="sm" glyph={false}>Доступ заморожен</Pill>
          : ctx.subscription_hold
            ? <Pill tone="accent" size="sm" glyph={false}>Подписка не активна — доступ на паузе</Pill>
            : null}
      </div>
      {fbClosed ? (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>Окно обратной связи: закрыто{ctx.feedback_until ? ` ${fmtDate(ctx.feedback_until)}` : ''}.
            Ученик не сможет переотправить работу — «на доработку» отправлять бессмысленно.</span>
        </div>
      ) : (
        <span className="t-caption text-mute">
          Окно обратной связи: {ctx.feedback_until ? `до ${fmtDate(ctx.feedback_until)}` : 'открыто'}
        </span>
      )}
    </Section>
  );
}

// Прошлая попытка — свёрнута по умолчанию: куратору нужен контекст, а не стена текста.
function PastAttempt({ attempt }) {
  const [open, setOpen] = useState(false);
  const reviews = attempt.reviews || [];
  return (
    <div className="acad-module">
      <button type="button" className="acad-module__head" aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ width: '100%', background: 'none', border: 0, cursor: 'pointer', textAlign: 'left', color: 'inherit' }}>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={15} />
        <span className="acad-section__title">Попытка {attempt.attempt_no}</span>
        <span className="acad-section__spacer" />
        {attempt.status && (
          <Pill tone={DEC_TONE[attempt.status] || 'neutral'} size="sm" glyph={false}>
            {SUB_LABEL[attempt.status] || attempt.status}</Pill>
        )}
        <span className="t-caption text-mute">{fmt(attempt.created_at)}</span>
      </button>
      {open && (
        <div className="acad-module__body stack stack-3">
          {attempt.body
            ? <div className="acad-submission__body">{attempt.body}</div>
            : <span className="t-caption text-mute">Текста в этой попытке не было.</span>}
          {(attempt.attachments || []).length > 0 && (
            <div className="acad-attach">
              {attempt.attachments.map((a) => (
                <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="acad-attach__chip">
                  <Icon name="file-text" size={13} />{a.name}</a>
              ))}
            </div>
          )}
          {reviews.length > 0
            ? <ReviewLog reviews={reviews} />
            : <span className="t-caption text-mute">Вердиктов по этой попытке нет.</span>}
        </div>
      )}
    </div>
  );
}

function ReviewDrawer({ submissionId, onClose, queueFilters = {}, onAdvance }) {
  const toast = useToast();
  const [nonce, setNonce] = useState(0);
  const q = useFakeQuery('academy-review-' + submissionId + '-' + nonce, () => api.getReview(submissionId), [submissionId, nonce]);
  const s = q.data;
  const [decision, setDecision] = useState('accepted');
  const [score, setScore] = useState('');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [busy, setBusy] = useState(false);
  // CU-3. Конвейер: сколько работ ещё ждёт (счётчик в шапке) и признак «очередь
  // кончилась» — вместо возврата в список после каждого вердикта.
  const [remaining, setRemaining] = useState(null);
  const [queueDone, setQueueDone] = useState(false);
  // Хотя бы один вердикт вынесен → при закрытии список надо перечитать.
  const [touched, setTouched] = useState(false);
  // CU-4: переоткрытие с ОБЯЗАТЕЛЬНОЙ причиной — открытое поле, а не тихая кнопка.
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const done = s && s.status !== 'submitted';
  // CU-5: «на доработку» и «отклонить» без комментария оставляют ученика с одним
  // словом «Результат» и без единой подсказки — такой вердикт на бэк не уходит.
  const commentRequired = decision !== 'accepted';

  // Конвейер переключает submissionId, НЕ размонтируя дровер: форму решения надо
  // сбрасывать руками, иначе балл и комментарий предыдущей работы переехали бы
  // на следующую.
  const [formFor, setFormFor] = useState(submissionId);
  if (formFor !== submissionId) {
    setFormFor(submissionId);
    setDecision('accepted'); setScore(''); setComment(''); setCommentError('');
    setReopenOpen(false); setReason(''); setReasonError('');
  }

  const pickDecision = useCallback((value) => {
    setDecision(value);
    if (value === 'accepted') setCommentError('');
  }, []);

  // Остаток очереди в шапке при открытии: куратор должен видеть объём работы
  // до первого вердикта, а не узнавать его по факту.
  useEffect(() => {
    let alive = true;
    api.fetchNextSubmission(queueFilters, null)
      .then((r) => { if (alive) setRemaining(r.remaining); })
      .catch(() => { /* счётчик — справка, его отсутствие не мешает проверять */ });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const submit = useCallback(async () => {
    if (commentRequired && !comment.trim()) {
      setCommentError(decision === 'revision'
        ? 'Опишите, что доработать'
        : 'Опишите, что доработать или почему работа отклонена');
      return;
    }
    setBusy(true);
    try {
      await api.reviewSubmission(submissionId, { decision, score: score === '' ? null : Number(score), comment });
      setTouched(true);
      toast.show('Решение сохранено', 'success');
      // CU-3: следующую работу берём тем же запросом очереди — проверенная из
      // выборки уже выпала, поэтому count в ответе и есть новый остаток.
      let next = null;
      try {
        const r = await api.fetchNextSubmission(queueFilters, submissionId);
        next = r.next; setRemaining(r.remaining);
      } catch { /* очередь недоступна — закрываемся, как раньше */ }
      if (next && onAdvance) onAdvance(next.id);
      else if (onAdvance) setQueueDone(true);
      else onClose(true);
    } catch (e) { toast.show(e?.detail || e?.message || 'Не удалось сохранить', 'danger'); } finally { setBusy(false); }
    // eslint-disable-next-line
  }, [submissionId, decision, score, comment, commentRequired, toast, onClose, onAdvance]);

  // CU-4. Откат ошибочного вердикта: работа возвращается в очередь, в журнале
  // появляется третья запись. Причина обязательна — её читает и ученик, и тот,
  // кто будет проверять работу следующим.
  const reopen = useCallback(async () => {
    if (!reason.trim()) { setReasonError('Укажите причину — она попадёт в журнал проверки'); return; }
    setBusy(true);
    try {
      await api.reopenReview(submissionId, reason.trim());
      setTouched(true);
      toast.show('Проверка переоткрыта — работа снова в очереди', 'success');
      setReopenOpen(false); setReason('');
      setNonce((n) => n + 1);
    } catch (e) { toast.show(e?.detail || e?.message || 'Не удалось переоткрыть', 'danger'); }
    finally { setBusy(false); }
  }, [submissionId, reason, toast]);

  const footer = (s && !done && !queueDone) ? (
    <div className="acad-decision-bar">
      <div className="acad-decision-bar__choices" role="radiogroup" aria-label="Решение">
        {DECISIONS.map((d) => (
          <Button key={d.value} role="radio" aria-checked={decision === d.value} className="acad-decision-choice"
            size="sm" variant={decision === d.value ? 'primary' : 'secondary'}
            onClick={() => pickDecision(d.value)}>{d.label}</Button>
        ))}
      </div>
      <div className="acad-decision-bar__row">
        <Field label={`Балл (из ${s.max_score})`} className="acad-decision-bar__score">
          <input className="input" type="number" min="0" max={s.max_score} value={score}
            onChange={(e) => setScore(e.target.value)} placeholder="—" />
        </Field>
        <Button className="acad-decision-bar__cta" variant="primary" loading={busy} onClick={submit}>Сохранить решение</Button>
      </div>
      {/* Поле комментария живёт выше по прокрутке — дублируем отказ в закреплённом
          футере, иначе куратор жмёт кнопку и не понимает, почему ничего не произошло. */}
      {commentError && (
        <div className="field__msg field__msg--error" role="alert">
          <Icon name="alert-circle" size={13} />{commentError}
        </div>
      )}
    </div>
  ) : null;

  // Очередь кончилась после последнего вердикта — конвейеру больше некуда вести.
  if (queueDone) {
    return (
      <Drawer open onClose={() => onClose(touched)} size="md" title="Проверка ДЗ">
        <EmptyState icon="check-circle" title="Очередь пуста"
          body="Все работы проверены. Новые появятся здесь, как только ученики их сдадут."
          action={<Button variant="primary" onClick={() => onClose(touched)}>Вернуться к списку</Button>} />
      </Drawer>
    );
  }

  return (
    <Drawer open onClose={() => onClose(touched)} size="md" footer={footer}
      title={s ? (s.student_label || 'Ученик') : 'Проверка ДЗ'}
      breadcrumbs={s ? `${s.course_title} · ${s.lesson_title}` : null}>
      <div>
        <QueryBoundary loading={q.loading && !s} error={q.error} onRetry={() => setNonce((n) => n + 1)}>
          {s && (
            <div className="stack stack-6">
              {/* CU-3: счётчик очереди прямо здесь — куратор проверяет подряд и
                  не возвращается в список, чтобы понять, сколько осталось. */}
              {remaining != null && (
                <div className="acad-queuebar">
                  <Icon name="layers" size={14} />
                  <span>На проверке осталось: <span className="acad-queuebar__count num">{remaining}</span></span>
                  <span className="acad-section__spacer" />
                  <span className="t-caption text-mute">
                    После решения откроется следующая работа
                  </span>
                </div>
              )}

              {done && (
                <div className="set-card__ok">
                  <span className="t-label">Уже проверено: {SUB_LABEL[s.status]}</span>
                  <Icon name="check-circle" size={16} />
                </div>
              )}

              {/* CU-4. Ошибочный вердикт откатывается — до сих пор «отклонил не
                  глядя» не исправлялось никем и ничем, и единственным выходом
                  была новая попытка ученика, доступная не всегда. */}
              {done && (
                <Section icon="refresh" title="Переоткрыть проверку">
                  {reopenOpen ? (
                    <div className="acad-reopen">
                      <Field label="Причина переоткрытия" error={reasonError}
                        hint="Попадёт в журнал проверки рядом с отменяемым вердиктом — вердикты не удаляются.">
                        <Textarea value={reason} minRows={2} invalid={!!reasonError}
                          onChange={(e) => { setReason(e.target.value); if (reasonError) setReasonError(''); }}
                          placeholder="Например: отклонил по ошибке — работа была в приложенном файле" />
                      </Field>
                      <div className="cluster cluster-8">
                        <Button variant="primary" size="sm" loading={busy} onClick={reopen}>
                          Переоткрыть</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setReopenOpen(false); setReasonError(''); }}>
                          Отмена</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
                      <Button variant="secondary" size="sm" iconLeft="refresh"
                        onClick={() => setReopenOpen(true)}>Переоткрыть проверку</Button>
                      <span className="t-caption text-mute">
                        Работа вернётся в очередь, вердикт останется в журнале как отменённый.
                      </span>
                    </div>
                  )}
                </Section>
              )}

              <StudentContext ctx={s.student_context} />

              {/* ST-5: заявки на пересдачу ТОГО ЖЕ ученика — прямо здесь, пока он
                  перед глазами. Иначе куратор закрывает дровер, идёт во вкладку
                  «Пересдачи» и ищет того же человека заново. Секция сама
                  исчезает, когда заявок нет (embedded). */}
              {s.student_context?.enrollment_id && (
                <RetakeQueue enrollmentId={s.student_context.enrollment_id}
                  courseId={s.course_id} embedded heading="Заявки на пересдачу теста" />
              )}

              {s.instructions_md && (
                <Section icon="edit" title="Задание">
                  <div className="hw-instructions md"><ChatMarkdown source={s.instructions_md} /></div>
                </Section>
              )}

              <Section icon="user" title="Ответ ученика" hint={`попытка ${s.attempt_no}`}>
                <div className="acad-submission">
                  {s.body && <div className="acad-submission__body">{s.body}</div>}
                  {(s.attachments || []).length > 0 && (
                    <div className="acad-attach">
                      {s.attachments.map((a) => (
                        <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="acad-attach__chip">
                          <Icon name="file-text" size={13} />{a.name}</a>
                      ))}
                    </div>
                  )}
                </div>
              </Section>

              {(s.past_attempts || []).length > 0 && (
                <Section icon="clock" title="Прошлые попытки" hint={s.past_attempts.length}>
                  <div className="stack stack-2">
                    {s.past_attempts.map((a) => <PastAttempt key={a.id ?? a.attempt_no} attempt={a} />)}
                  </div>
                </Section>
              )}

              {(s.reviews || []).length > 0 && (
                <Section icon="clock" title="История проверок">
                  <ReviewLog reviews={s.reviews} />
                </Section>
              )}

              {!done && (
                <Section icon="edit" title="Комментарий ученику">
                  <Field error={commentError}
                    hint={commentRequired
                      ? 'Обязателен для «на доработку» и «отклонить» — ученик должен понять, что делать дальше.'
                      : 'Необязателен при приёме работы.'}>
                    <Textarea value={comment} minRows={3}
                      invalid={!!commentError}
                      onChange={(e) => { setComment(e.target.value); if (commentError) setCommentError(''); }}
                      placeholder="Обратная связь по работе…" />
                  </Field>
                </Section>
              )}
            </div>
          )}
        </QueryBoundary>
      </div>
    </Drawer>
  );
}

export { ReviewDrawer };
